"use client";

import { cn } from "@guru/shared/utils/cn";
import { motion, useReducedMotion } from "motion/react";

/**
 * Animated, on-brand illustrations for the roadmap surfaces — each one is a
 * tiny product moment rather than a flat icon. Motion is always-on but gentle;
 * under prefers-reduced-motion every scene resolves to a clean static frame so
 * nothing moves.
 *
 * Palette mirrors the ink & saffron system: navy = structure/data,
 * saffron = the active highlight, green = paid/confirmed, rose = due.
 */

const INK = "var(--brand-navy)";
const SAFFRON = "var(--brand-saffron)";
const POSITIVE = "var(--brand-positive)";
const DUE = "var(--brand-due)";

export type GlyphName =
  | "invoice"
  | "analytics"
  | "files"
  | "calendar"
  | "parent"
  | "whatsapp"
  | "link"
  | "branch"
  | "channels"
  | "wallet";

export type GlyphTone = "live" | "building" | "planned";

interface SceneProps {
  reduced: boolean;
}

/**
 * Ambient loop transition — infinite under normal motion, an instant no-op
 * under prefers-reduced-motion so reduced-motion users get the resolved frame
 * with no scheduled animation.
 */
const loop = (reduced: boolean, duration: number, delay = 0, repeatDelay = 0) =>
  reduced
    ? { duration: 0 }
    : {
        duration,
        delay,
        repeat: Number.POSITIVE_INFINITY,
        repeatDelay,
        ease: "easeInOut" as const,
      };

/* ------------------------------------------------------------------ *
 * Scenes — viewBox is a shared 240 × 120 canvas.
 * ------------------------------------------------------------------ */

/** A consolidated invoice: a coin drops, the sheet marks itself Paid. */
function InvoiceScene({ reduced }: SceneProps) {
  return (
    <g>
      {/* Sheet shadow + sheet */}
      <rect x="64" y="24" width="112" height="78" rx="8" fill={INK} opacity="0.08" />
      <rect
        x="60"
        y="20"
        width="112"
        height="78"
        rx="8"
        fill="var(--card)"
        stroke={INK}
        strokeOpacity="0.18"
        strokeWidth="1.5"
      />
      {/* Header pill */}
      <rect x="72" y="31" width="46" height="9" rx="4.5" fill={SAFFRON} opacity="0.85" />
      <rect x="124" y="31" width="36" height="9" rx="4.5" fill={INK} opacity="0.12" />
      {/* Ruled lines */}
      {[52, 63, 74].map((y, i) => (
        <rect
          key={y}
          x="72"
          y={y}
          width={i === 2 ? 50 : 88}
          height="5"
          rx="2.5"
          fill={INK}
          opacity="0.14"
        />
      ))}
      {/* Total line */}
      <rect x="72" y="84" width="40" height="6" rx="3" fill={INK} opacity="0.28" />

      {/* Coin dropping onto the sheet */}
      <motion.g
        initial={false}
        animate={reduced ? { y: 0, opacity: 1 } : { y: [-34, 0], opacity: [0, 1, 1] }}
        transition={loop(reduced, 2.4, 0, 1.4)}
      >
        <circle cx="150" cy="58" r="14" fill={SAFFRON} />
        <circle
          cx="150"
          cy="58"
          r="14"
          fill="none"
          stroke={INK}
          strokeOpacity="0.25"
          strokeWidth="1.5"
        />
        <text
          x="150"
          y="63"
          textAnchor="middle"
          fontSize="16"
          fontWeight="700"
          fill="var(--brand-saffron-contrast)"
        >
          ₹
        </text>
      </motion.g>

      {/* Paid stamp — pops in once the coin lands. Centred at (110, 84) and
          tilted -8°; sized so its lowest rotated corner stays above the sheet
          bottom (98) and its right edge clears the coin. */}
      <motion.g
        initial={false}
        animate={reduced ? { scale: 1, opacity: 1 } : { scale: [0.4, 1.12, 1], opacity: [0, 1, 1] }}
        transition={loop(reduced, 2.4, 1.1, 1.4)}
        style={{ transformOrigin: "110px 84px" }}
      >
        <g transform="rotate(-8 110 84)">
          <rect
            x="87"
            y="75"
            width="46"
            height="18"
            rx="5"
            fill={POSITIVE}
            fillOpacity="0.14"
            stroke={POSITIVE}
            strokeWidth="1.5"
          />
          <path
            d="M95 84 l3.5 3.5 l6 -7"
            fill="none"
            stroke={POSITIVE}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <text x="116" y="88" textAnchor="middle" fontSize="10" fontWeight="700" fill={POSITIVE}>
            PAID
          </text>
        </g>
      </motion.g>
    </g>
  );
}

/** Fee analytics: bars grow, a trend line draws across the tops. */
function AnalyticsScene({ reduced }: SceneProps) {
  const bars = [
    { x: 60, h: 26, c: INK },
    { x: 84, h: 38, c: INK },
    { x: 108, h: 32, c: INK },
    { x: 132, h: 52, c: INK },
    { x: 156, h: 64, c: SAFFRON },
  ];
  const baseY = 96;
  return (
    <g>
      {/* Axis */}
      <line
        x1="50"
        y1={baseY}
        x2="190"
        y2={baseY}
        stroke={INK}
        strokeOpacity="0.2"
        strokeWidth="1.5"
      />
      {bars.map((b, i) => (
        <motion.rect
          key={b.x}
          x={b.x}
          width="14"
          rx="3"
          fill={b.c}
          fillOpacity={b.c === SAFFRON ? 1 : 0.85}
          initial={false}
          animate={
            reduced
              ? { height: b.h, y: baseY - b.h }
              : { height: [4, b.h], y: [baseY - 4, baseY - b.h] }
          }
          transition={loop(reduced, 1.6, i * 0.12, 1.4)}
        />
      ))}
      {/* Trend line + leading dot */}
      <motion.path
        d="M67 70 L91 58 L115 64 L139 44 L163 32"
        fill="none"
        stroke={SAFFRON}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={false}
        animate={
          reduced ? { pathLength: 1, opacity: 1 } : { pathLength: [0, 1], opacity: [0.2, 1] }
        }
        transition={loop(reduced, 1.8, 0.4, 1.2)}
      />
      <motion.circle
        cx="163"
        cy="32"
        r="4.5"
        fill={SAFFRON}
        stroke="var(--card)"
        strokeWidth="2"
        initial={false}
        animate={reduced ? { scale: 1, opacity: 1 } : { scale: [0, 1.2, 1], opacity: [0, 1] }}
        transition={loop(reduced, 1.8, 1.4, 1.2)}
        style={{ transformOrigin: "163px 32px" }}
      />
      {/* Up chip */}
      <g transform="translate(168 18)">
        <path d="M0 6 l5 -6 l5 6 z" fill={POSITIVE} />
      </g>
    </g>
  );
}

/** File sharing: a document slides from the folder to a recipient. */
function FilesScene({ reduced }: SceneProps) {
  return (
    <g>
      {/* Dotted travel path */}
      <line
        x1="86"
        y1="58"
        x2="168"
        y2="58"
        stroke={INK}
        strokeOpacity="0.25"
        strokeWidth="1.5"
        strokeDasharray="3 5"
        strokeLinecap="round"
      />

      {/* Folder */}
      <path
        d="M44 46 h16 l5 6 h19 a4 4 0 0 1 4 4 v26 a4 4 0 0 1 -4 4 h-40 a4 4 0 0 1 -4 -4 v-32 a4 4 0 0 1 4 -4 z"
        fill={SAFFRON}
        fillOpacity="0.18"
        stroke={SAFFRON}
        strokeWidth="1.5"
      />
      {/* Stacked docs inside the folder */}
      <rect
        x="52"
        y="52"
        width="30"
        height="34"
        rx="3"
        fill="var(--card)"
        stroke={INK}
        strokeOpacity="0.2"
        strokeWidth="1.3"
      />

      {/* Travelling document */}
      <motion.g
        initial={false}
        animate={reduced ? { x: 86, opacity: 1 } : { x: [0, 86], opacity: [0, 1, 1, 0] }}
        transition={loop(reduced, 2.6, 0, 1)}
      >
        <rect
          x="52"
          y="48"
          width="30"
          height="34"
          rx="3"
          fill="var(--card)"
          stroke={SAFFRON}
          strokeWidth="1.6"
        />
        {[55, 62, 69].map((y) => (
          <rect key={y} x="57" y={y} width="20" height="3.4" rx="1.7" fill={INK} opacity="0.2" />
        ))}
      </motion.g>

      {/* Recipient avatar */}
      <circle
        cx="178"
        cy="58"
        r="17"
        fill={INK}
        fillOpacity="0.1"
        stroke={INK}
        strokeOpacity="0.25"
        strokeWidth="1.5"
      />
      <circle cx="178" cy="52" r="6" fill={INK} fillOpacity="0.55" />
      <path d="M168 70 a10 9 0 0 1 20 0" fill={INK} fillOpacity="0.55" />
      {/* Delivered check */}
      <motion.g
        initial={false}
        animate={reduced ? { scale: 1, opacity: 1 } : { scale: [0, 1.2, 1], opacity: [0, 0, 1] }}
        transition={loop(reduced, 2.6, 1.2, 1)}
        style={{ transformOrigin: "192px 44px" }}
      >
        <circle cx="192" cy="44" r="8" fill={POSITIVE} />
        <path
          d="M188 44 l3 3 l5 -6"
          fill="none"
          stroke="var(--card)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </motion.g>
    </g>
  );
}

/** Scheduling & attendance: ticks cascade across calendar cells. */
function CalendarScene({ reduced }: SceneProps) {
  const cols = 4;
  const rows = 3;
  // Body and its inner padding; cell metrics derive from this so every cell
  // (and its tick) stays inside the calendar border.
  const bodyX = 50;
  const bodyY = 18;
  const bodyW = 140;
  const bodyH = 100;
  const headerH = 16;
  const padX = 11;
  const padTop = 7;
  const padBottom = 9;
  const gap = 6;
  const gridX = bodyX + padX;
  const gridTop = bodyY + headerH + padTop;
  const gridW = bodyW - padX * 2;
  const gridH = bodyH - headerH - padTop - padBottom;
  const cellW = (gridW - (cols - 1) * gap) / cols;
  const cellH = (gridH - (rows - 1) * gap) / rows;
  const cells = Array.from({ length: cols * rows });
  return (
    <g>
      {/* Calendar body */}
      <rect
        x={bodyX}
        y={bodyY}
        width={bodyW}
        height={bodyH}
        rx="9"
        fill="var(--card)"
        stroke={INK}
        strokeOpacity="0.18"
        strokeWidth="1.5"
      />
      {/* Header bar (rounded top, square bottom strip) */}
      <rect x={bodyX} y={bodyY} width={bodyW} height="14" rx="9" fill={SAFFRON} opacity="0.85" />
      <rect
        x={bodyX}
        y={bodyY + 8}
        width={bodyW}
        height={headerH - 8}
        fill={SAFFRON}
        opacity="0.85"
      />
      {/* Binder hooks */}
      <circle cx={bodyX + 22} cy={bodyY - 2} r="2.5" fill={INK} opacity="0.4" />
      <circle cx={bodyX + bodyW - 22} cy={bodyY - 2} r="2.5" fill={INK} opacity="0.4" />

      {cells.map((_, i) => {
        const cx = gridX + (i % cols) * (cellW + gap);
        const cy = gridTop + Math.floor(i / cols) * (cellH + gap);
        const mx = cx + cellW / 2;
        const my = cy + cellH / 2;
        const present = i % 5 !== 3; // a couple of "absent" cells
        return (
          <g key={`cell-${cx}-${cy}`}>
            <rect
              x={cx}
              y={cy}
              width={cellW}
              height={cellH}
              rx="4"
              fill={present ? POSITIVE : DUE}
              fillOpacity="0.08"
              stroke={INK}
              strokeOpacity="0.1"
              strokeWidth="1"
            />
            {present ? (
              <motion.path
                d={`M${mx - 5} ${my} l3 3 l6 -7`}
                fill="none"
                stroke={POSITIVE}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={false}
                animate={
                  reduced ? { pathLength: 1, opacity: 1 } : { pathLength: [0, 1], opacity: [0, 1] }
                }
                transition={loop(reduced, 1.2, 0.5 + i * 0.12, 1.6)}
              />
            ) : (
              <line
                x1={mx - 4}
                y1={my - 4}
                x2={mx + 4}
                y2={my + 4}
                stroke={DUE}
                strokeOpacity="0.5"
                strokeWidth="2"
                strokeLinecap="round"
              />
            )}
          </g>
        );
      })}
    </g>
  );
}

/** Parent updates: a progress card with a drawing sparkline + twinkling stars. */
function ParentScene({ reduced }: SceneProps) {
  // Each star spans ≈±6 around its x; the card's right edge is 184, so the
  // rightmost centre stays ≤ 172 to keep the points inside the border.
  const stars = [
    { x: 144, y: 40 },
    { x: 158, y: 40 },
    { x: 172, y: 40 },
  ];
  return (
    <g>
      <rect
        x="56"
        y="26"
        width="128"
        height="72"
        rx="9"
        fill="var(--card)"
        stroke={INK}
        strokeOpacity="0.18"
        strokeWidth="1.5"
      />
      {/* Avatar + name lines */}
      <circle
        cx="76"
        cy="46"
        r="11"
        fill={SAFFRON}
        fillOpacity="0.22"
        stroke={SAFFRON}
        strokeWidth="1.4"
      />
      <circle cx="76" cy="43" r="4" fill={SAFFRON} />
      <path d="M69 53 a7 6 0 0 1 14 0" fill={SAFFRON} />
      <rect x="94" y="40" width="40" height="6" rx="3" fill={INK} opacity="0.25" />
      <rect x="94" y="50" width="26" height="5" rx="2.5" fill={INK} opacity="0.14" />

      {/* Twinkling stars */}
      {stars.map((s, i) => (
        <motion.path
          key={s.x}
          d={`M${s.x} ${s.y - 6} l1.8 3.7 l4.1 0.6 l-3 2.9 l0.7 4 l-3.6 -1.9 l-3.6 1.9 l0.7 -4 l-3 -2.9 l4.1 -0.6 z`}
          fill={SAFFRON}
          initial={false}
          animate={
            reduced ? { scale: 1, opacity: 1 } : { scale: [0.6, 1, 0.6], opacity: [0.4, 1, 0.4] }
          }
          transition={loop(reduced, 2, i * 0.25)}
          style={{ transformOrigin: `${s.x}px ${s.y}px` }}
        />
      ))}

      {/* Progress sparkline */}
      <motion.path
        d="M68 86 L88 80 L108 82 L128 70 L150 66 L172 58"
        fill="none"
        stroke={POSITIVE}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={false}
        animate={
          reduced ? { pathLength: 1, opacity: 1 } : { pathLength: [0, 1], opacity: [0.3, 1] }
        }
        transition={loop(reduced, 2, 0.4, 1)}
      />
    </g>
  );
}

/** WhatsApp reminders: a bubble whose ticks turn read, then a paid confirmation. */
function WhatsappScene({ reduced }: SceneProps) {
  return (
    <g>
      {/* Reminder bubble */}
      <path
        d="M54 30 h108 a10 10 0 0 1 10 10 v22 a10 10 0 0 1 -10 10 h-94 l-12 10 v-10 h-2 a10 10 0 0 1 -10 -10 v-22 a10 10 0 0 1 10 -10 z"
        fill="#dcf8c6"
        stroke={POSITIVE}
        strokeOpacity="0.3"
        strokeWidth="1.4"
      />
      <rect x="62" y="40" width="74" height="6" rx="3" fill={INK} opacity="0.3" />
      <rect x="62" y="52" width="56" height="6" rx="3" fill={INK} opacity="0.18" />
      {/* Double ticks turning read (grey → WhatsApp blue) */}
      <motion.path
        d="M150 56 l3 3 l7 -8 M156 56 l3 3 l7 -8"
        fill="none"
        stroke="#9aa5a0"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={false}
        animate={reduced ? { stroke: "#34b7f1" } : { stroke: ["#9aa5a0", "#9aa5a0", "#34b7f1"] }}
        transition={loop(reduced, 2.4, 0, 0.6)}
      />

      {/* Payment received chip */}
      <motion.g
        initial={false}
        animate={reduced ? { y: 0, opacity: 1 } : { y: [10, 0], opacity: [0, 0, 1] }}
        transition={loop(reduced, 2.4, 0.8, 0.6)}
      >
        <rect
          x="92"
          y="84"
          width="96"
          height="24"
          rx="12"
          fill={POSITIVE}
          fillOpacity="0.14"
          stroke={POSITIVE}
          strokeWidth="1.4"
        />
        <circle cx="106" cy="96" r="7" fill={POSITIVE} />
        <path
          d="M102.5 96 l2.5 2.5 l4.5 -5"
          fill="none"
          stroke="var(--card)"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <text x="118" y="100" fontSize="11" fontWeight="700" fill={POSITIVE}>
          ₹ received
        </text>
      </motion.g>
    </g>
  );
}

/** Self-renewing payment links: chain rings with a sweeping refresh arc. */
function LinkScene({ reduced }: SceneProps) {
  return (
    <g>
      {/* Refresh arc orbiting the link */}
      <motion.g
        initial={false}
        animate={reduced ? { rotate: 0 } : { rotate: 360 }}
        transition={
          reduced
            ? { duration: 0 }
            : { duration: 6, repeat: Number.POSITIVE_INFINITY, ease: "linear" }
        }
        style={{ transformOrigin: "120px 60px" }}
      >
        <path
          d="M120 22 a38 38 0 1 1 -27 11"
          fill="none"
          stroke={SAFFRON}
          strokeOpacity="0.55"
          strokeWidth="2.4"
          strokeLinecap="round"
        />
        <path d="M120 14 l2 8 l-9 1 z" fill={SAFFRON} />
      </motion.g>

      {/* Chain link */}
      <motion.g
        initial={false}
        animate={reduced ? { scale: 1 } : { scale: [1, 1.06, 1] }}
        transition={loop(reduced, 2.4)}
        style={{ transformOrigin: "120px 60px" }}
      >
        <rect
          x="92"
          y="50"
          width="34"
          height="22"
          rx="11"
          fill="none"
          stroke={INK}
          strokeWidth="5"
          transform="rotate(-30 109 61)"
        />
        <rect
          x="114"
          y="50"
          width="34"
          height="22"
          rx="11"
          fill="none"
          stroke={SAFFRON}
          strokeWidth="5"
          transform="rotate(-30 131 61)"
        />
      </motion.g>

      {/* Pulse travelling along the link */}
      <motion.circle
        r="3.5"
        fill={POSITIVE}
        initial={false}
        animate={
          reduced
            ? { opacity: 1, cx: 120, cy: 60 }
            : { opacity: [0, 1, 0], cx: [98, 142], cy: [68, 54] }
        }
        transition={loop(reduced, 2.4, 0.4, 0.6)}
      />
    </g>
  );
}

/** Multi-branch operations: a root rolls up into branched nodes. */
function BranchScene({ reduced }: SceneProps) {
  const children = [
    { x: 170, y: 36 },
    { x: 170, y: 60 },
    { x: 170, y: 84 },
  ];
  return (
    <g>
      {/* Rollup pill */}
      <motion.g
        initial={false}
        animate={reduced ? { opacity: 1, y: 0 } : { opacity: [0, 1], y: [-6, 0] }}
        transition={loop(reduced, 2.6, 1.4, 0.8)}
      >
        <rect
          x="42"
          y="14"
          width="74"
          height="20"
          rx="10"
          fill={SAFFRON}
          fillOpacity="0.16"
          stroke={SAFFRON}
          strokeWidth="1.4"
        />
        <text
          x="79"
          y="28"
          textAnchor="middle"
          fontSize="11"
          fontWeight="700"
          fill="var(--brand-saffron-text)"
        >
          ↑ rollup
        </text>
      </motion.g>

      {/* Connector lines */}
      {children.map((c, i) => (
        <motion.path
          key={`line-${c.y}`}
          d={`M70 60 C120 60 120 ${c.y} ${c.x - 14} ${c.y}`}
          fill="none"
          stroke={INK}
          strokeOpacity="0.3"
          strokeWidth="1.8"
          initial={false}
          animate={reduced ? { pathLength: 1 } : { pathLength: [0, 1] }}
          transition={loop(reduced, 2.6, i * 0.18, 0.8)}
        />
      ))}

      {/* Root node */}
      <circle cx="64" cy="60" r="13" fill={SAFFRON} />
      <circle
        cx="64"
        cy="60"
        r="13"
        fill="none"
        stroke={INK}
        strokeOpacity="0.2"
        strokeWidth="1.4"
      />
      <path
        d="M58 60 h12 M64 54 v12"
        stroke="var(--brand-saffron-contrast)"
        strokeWidth="2"
        strokeLinecap="round"
      />

      {/* Child nodes */}
      {children.map((c, i) => (
        <motion.g
          key={`node-${c.y}`}
          initial={false}
          animate={reduced ? { scale: 1, opacity: 1 } : { scale: [0, 1.1, 1], opacity: [0, 1] }}
          transition={loop(reduced, 2.6, 0.6 + i * 0.18, 0.8)}
          style={{ transformOrigin: `${c.x}px ${c.y}px` }}
        >
          <rect
            x={c.x - 14}
            y={c.y - 9}
            width="34"
            height="18"
            rx="5"
            fill="var(--card)"
            stroke={INK}
            strokeOpacity="0.25"
            strokeWidth="1.3"
          />
          <rect x={c.x - 9} y={c.y - 3} width="18" height="4" rx="2" fill={INK} opacity="0.3" />
        </motion.g>
      ))}
    </g>
  );
}

/** More reminder channels: a hub broadcasts to WhatsApp, SMS and email. */
function ChannelsScene({ reduced }: SceneProps) {
  const chips = [
    { x: 176, y: 32, c: POSITIVE },
    { x: 188, y: 60, c: SAFFRON },
    { x: 176, y: 88, c: INK },
  ];
  return (
    <g>
      {/* Radar rings expanding from the hub */}
      {[0, 1, 2].map((i) => (
        <motion.circle
          key={`ring-${i}`}
          cx="64"
          cy="60"
          fill="none"
          stroke={SAFFRON}
          strokeWidth="1.6"
          initial={false}
          animate={reduced ? { r: 18 + i * 14, opacity: 0.25 } : { r: [14, 56], opacity: [0.5, 0] }}
          transition={loop(reduced, 2.4, i * 0.8)}
        />
      ))}

      {/* Connectors to chips */}
      {chips.map((c) => (
        <line
          key={`conn-${c.y}`}
          x1="64"
          y1="60"
          x2={c.x}
          y2={c.y}
          stroke={INK}
          strokeOpacity="0.18"
          strokeWidth="1.4"
          strokeDasharray="2 4"
        />
      ))}

      {/* Hub */}
      <circle cx="64" cy="60" r="16" fill={SAFFRON} />
      <path d="M56 58 a8 8 0 0 1 16 0 v5 l2 3 h-20 l2 -3 z" fill="var(--brand-saffron-contrast)" />
      <circle cx="64" cy="71" r="2.4" fill="var(--brand-saffron-contrast)" />

      {/* Channel chips lighting up in sequence */}
      {chips.map((c, i) => (
        <motion.circle
          key={`chip-${c.y}`}
          cx={c.x}
          cy={c.y}
          r="9"
          fill={c.c}
          initial={false}
          animate={
            reduced ? { scale: 1, opacity: 1 } : { scale: [0.7, 1.15, 0.9], opacity: [0.5, 1, 0.7] }
          }
          transition={loop(reduced, 2.4, i * 0.6)}
          style={{ transformOrigin: `${c.x}px ${c.y}px` }}
        />
      ))}
    </g>
  );
}

/** Expense management: coins slide into a wallet beside the ledger. */
function WalletScene({ reduced }: SceneProps) {
  return (
    <g>
      {/* Wallet body */}
      <rect
        x="48"
        y="46"
        width="96"
        height="58"
        rx="10"
        fill={INK}
        fillOpacity="0.12"
        stroke={INK}
        strokeOpacity="0.3"
        strokeWidth="1.5"
      />
      <rect x="48" y="58" width="96" height="34" rx="6" fill={INK} fillOpacity="0.18" />
      {/* Clasp */}
      <rect
        x="120"
        y="68"
        width="30"
        height="16"
        rx="8"
        fill="var(--card)"
        stroke={INK}
        strokeOpacity="0.3"
        strokeWidth="1.4"
      />
      <circle cx="135" cy="76" r="3.4" fill={SAFFRON} />

      {/* Coins sliding in */}
      {[0, 1].map((i) => (
        <motion.g
          key={`coin-${i}`}
          initial={false}
          animate={reduced ? { x: 0, opacity: 1 } : { x: [60, 0], opacity: [0, 1, 1, 0] }}
          transition={loop(reduced, 2.6, i * 0.5, 0.8)}
        >
          <circle cx="96" cy={42 - i * 2} r="11" fill={SAFFRON} />
          <text
            x="96"
            y={47 - i * 2}
            textAnchor="middle"
            fontSize="12"
            fontWeight="700"
            fill="var(--brand-saffron-contrast)"
          >
            ₹
          </text>
        </motion.g>
      ))}

      {/* Mini ledger column */}
      <g>
        <line
          x1="166"
          y1="34"
          x2="166"
          y2="100"
          stroke={INK}
          strokeOpacity="0.18"
          strokeWidth="1.4"
        />
        {[
          { y: 42, c: POSITIVE, s: "+" },
          { y: 60, c: DUE, s: "−" },
          { y: 78, c: POSITIVE, s: "+" },
        ].map((row) => (
          <g key={row.y}>
            <circle
              cx="166"
              cy={row.y}
              r="5"
              fill={row.c}
              fillOpacity="0.18"
              stroke={row.c}
              strokeWidth="1.3"
            />
            <text
              x="166"
              y={row.y + 3.5}
              textAnchor="middle"
              fontSize="9"
              fontWeight="700"
              fill={row.c}
            >
              {row.s}
            </text>
            <rect
              x="176"
              y={row.y - 2.5}
              width="14"
              height="5"
              rx="2.5"
              fill={INK}
              opacity="0.16"
            />
          </g>
        ))}
      </g>
    </g>
  );
}

const SCENES: Record<GlyphName, (props: SceneProps) => React.JSX.Element> = {
  invoice: InvoiceScene,
  analytics: AnalyticsScene,
  files: FilesScene,
  calendar: CalendarScene,
  parent: ParentScene,
  whatsapp: WhatsappScene,
  link: LinkScene,
  branch: BranchScene,
  channels: ChannelsScene,
  wallet: WalletScene,
};

const toneFrame: Record<GlyphTone, string> = {
  live: "border-positive/25 bg-gradient-to-br from-positive/[0.07] via-card to-card",
  building: "border-brand/30 bg-gradient-to-br from-brand/[0.1] via-card to-card",
  planned: "border-border bg-gradient-to-br from-ink/[0.05] via-card to-card",
};

const toneGlow: Record<GlyphTone, string> = {
  live: "bg-positive/25",
  building: "bg-brand/35",
  planned: "bg-ink/15",
};

interface FeatureGlyphProps {
  name: GlyphName;
  tone?: GlyphTone;
  className?: string;
}

/**
 * Frames a scene with a tinted gradient, a faint ledger texture and a soft
 * corner glow so the illustration reads as a small lit diorama rather than a
 * flat icon. Decorative only — callers supply the accessible label.
 */
export function FeatureGlyph({ name, tone = "planned", className }: FeatureGlyphProps) {
  const reduced = !!useReducedMotion();
  const Scene = SCENES[name] ?? InvoiceScene;

  return (
    <div
      className={cn(
        "relative isolate h-28 w-full overflow-hidden rounded-xl border",
        toneFrame[tone],
        className
      )}
    >
      <div className="ledger-grid absolute inset-0 opacity-50" aria-hidden="true" />
      <div
        className={cn("absolute -left-8 -top-10 h-28 w-28 rounded-full blur-2xl", toneGlow[tone])}
        aria-hidden="true"
      />
      <svg
        viewBox="0 0 240 120"
        className="relative z-10 h-full w-full transition-transform duration-500 group-hover:scale-[1.04]"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <Scene reduced={reduced} />
      </svg>
    </div>
  );
}
